import { zodResolver } from '@hookform/resolvers/zod'
import { UserPlus, X } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ROLE } from '@ankor-io/common/domain/iam/account'
import { Role } from '@ankor-io/common/types/account'

import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogTitle } from '@/ui-components/Dialog'
import { ErrorMessage } from '@/ui-components/Fieldset'
import { Input } from '@/ui-components/Input'
import { Select } from '@/ui-components/Select'

type InviteUserModalProps = {
  isOpen: boolean
  enforceInviteToConnectedAccountId?: string
  onClose: () => void
  onInvite: (userData: { email: string; role: Role; connectedAccountId?: string }) => void
}

const formSchema = z.object({
  email: z.string().email({
    message: 'Invalid email address.',
  }),
  role: z.string().min(1, {
    message: 'Please select a role for the user.',
  }),
  connectedAccountId: z.string().optional(),
})

const InviteUserModal = ({ isOpen, enforceInviteToConnectedAccountId, onClose, onInvite }: InviteUserModalProps) => {
  const { activeOrganizationRole } = useUserContext()
  const { connectedAccounts } = useConnectedAccount()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      role: '',
      connectedAccountId: enforceInviteToConnectedAccountId || '',
    },
  })

  const handleInvite = (values: z.infer<typeof formSchema>) => {
    onInvite({
      email: values.email,
      role: values.role as Role,
      connectedAccountId: values.connectedAccountId,
    })
    form.reset()
    onClose()
  }

  const handleCancel = () => {
    form.reset()
    onClose()
  }

  const isRoleInvitableByUser = (invitingRole: Role) => {
    const currentRole = ROLE(activeOrganizationRole)
    return currentRole.isEqualOrHigherThan(invitingRole)
  }

  if (!isOpen) return null

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <UserPlus className="size-5 text-gray-900 dark:text-white" />
          <DialogTitle>Invite User</DialogTitle>
        </div>
        <Button
          plain
          onClick={handleCancel}
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <form
        onSubmit={form.handleSubmit(handleInvite)}
        className="space-y-4"
      >
        <div>
          <label
            htmlFor="email"
            className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="Enter user email"
            autoComplete="off"
            {...form.register('email')}
          />
          {form.formState.errors.email && <ErrorMessage>{form.formState.errors.email.message}</ErrorMessage>}
        </div>

        <div>
          <label
            htmlFor="role"
            className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Role
          </label>
          <Select
            id="role"
            {...form.register('role')}
          >
            <option value="">Select a role</option>
            {Object.values(Role).map((role) => (
              <option
                key={role}
                value={role}
                disabled={!isRoleInvitableByUser(role)}
              >
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </Select>
          {form.formState.errors.role && <ErrorMessage>{form.formState.errors.role.message}</ErrorMessage>}
        </div>

        <div>
          <label
            htmlFor="connectedAccount"
            className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Vessel
          </label>
          <Select
            id="connectedAccount"
            disabled={!!enforceInviteToConnectedAccountId}
            {...form.register('connectedAccountId')}
          >
            <option value="">Select a vessel</option>
            {connectedAccounts.map((connectedAccount) => (
              <option
                key={connectedAccount.id}
                value={connectedAccount.id}
              >
                {connectedAccount.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex justify-end space-x-2 pt-6">
          <Button
            outline
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send Invite
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export default InviteUserModal
